import React, { useState } from 'react';
import { Container, Row, Col, Card, Button, Form, ListGroup, Image, Alert } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { FaTrash, FaArrowLeft, FaShoppingCart, FaCreditCard } from 'react-icons/fa';
import Layout from '../components/layout/Layout';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';

const Cart = () => {
  const { cartItems, removeFromCart, updateQuantity, clearCart, getTotalPrice } = useCart();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  console.log('Cart page - cartItems:', cartItems);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const handleQuantityChange = (productId, quantity) => {
    updateQuantity(productId, parseInt(quantity));
  };
  
  const handleRemoveItem = (productId) => {
    removeFromCart(productId);
  };
  
  const handleCheckout = () => {
    if (!currentUser) {
      setError('Please log in to checkout');
      return;
    }
    
    if (cartItems.length === 0) {
      setError('Your cart is empty');
      return;
    }
    
    // In a real app, this would navigate to a checkout page
    navigate('/checkout');
  };
  
  return (
    <Layout>
      <Container className="py-4">
        <h2 className="mb-4">
          <FaShoppingCart className="me-2" />
          Your Shopping Cart
        </h2>
        
        {error && <Alert variant="danger">{error}</Alert>}
        
        {cartItems.length === 0 ? (
          <Card className="text-center p-5 shadow-sm">
            <Card.Body>
              <h3>Your cart is empty</h3>
              <p className="text-muted">
                Looks like you haven't added any products to your cart yet.
              </p>
              <Button 
                as={Link} 
                to="/products" 
                variant="primary"
                size="lg"
                className="mt-3"
              >
                <FaArrowLeft className="me-2" />
                Continue Shopping
              </Button>
            </Card.Body>
          </Card>
        ) : (
          <Row>
            {/* Cart Items */}
            <Col lg={8}>
              <Card className="shadow-sm mb-4">
                <Card.Body>
                  <ListGroup variant="flush">
                    {cartItems.map(item => (
                      <ListGroup.Item key={item.id} className="py-3">
                        <Row className="align-items-center">
                          <Col xs={3} md={2}>
                            <Image 
                              src={item.images && item.images.length > 0 ? item.images[0].image : 'https://via.placeholder.com/100x100?text=No+Image'} 
                              alt={item.name} 
                              fluid 
                              thumbnail
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'https://via.placeholder.com/100x100?text=No+Image';
                              }}
                            />
                          </Col>
                          <Col xs={9} md={4}>
                            <h5>
                              <Link to={`/products/${item.id}`} className="text-decoration-none">
                                {item.name}
                              </Link>
                            </h5>
                            <div className="text-muted small">
                              Seller: {item.seller?.name || 'Unknown'}
                            </div>
                            <div className="text-primary">
                              GH₵ {Number(item.price).toFixed(2)}
                            </div>
                          </Col>
                          <Col xs={6} md={3} className="mt-3 mt-md-0">
                            <Form.Group controlId={`quantity-${item.id}`}>
                              <Form.Label>Quantity</Form.Label>
                              <Form.Control
                                type="number"
                                min="1"
                                max={item.stock || 10}
                                value={item.quantity}
                                onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                              />
                            </Form.Group>
                          </Col>
                          <Col xs={6} md={2} className="text-end mt-3 mt-md-0">
                            <div className="fw-bold mb-2">
                              GH₵ {(Number(item.price) * item.quantity).toFixed(2)}
                            </div>
                            <Button 
                              variant="outline-danger" 
                              size="sm"
                              onClick={() => handleRemoveItem(item.id)}
                            >
                              <FaTrash /> Remove
                            </Button>
                          </Col>
                        </Row>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </Card.Body>
                <Card.Footer className="bg-white d-flex justify-content-between">
                  <Button 
                    as={Link} 
                    to="/products" 
                    variant="outline-primary"
                  >
                    <FaArrowLeft className="me-2" />
                    Continue Shopping
                  </Button>
                  <Button 
                    variant="outline-danger"
                    onClick={() => clearCart()}
                  >
                    <FaTrash className="me-2" />
                    Clear Cart
                  </Button>
                </Card.Footer>
              </Card>
            </Col>
            
            {/* Order Summary */}
            <Col lg={4}>
              <Card className="shadow-sm">
                <Card.Header className="bg-white">
                  <h4 className="mb-0">Order Summary</h4>
                </Card.Header>
                <Card.Body>
                  <ListGroup variant="flush">
                    <ListGroup.Item className="d-flex justify-content-between px-0">
                      <span>Subtotal ({cartItems.reduce((acc, item) => acc + item.quantity, 0)} items)</span>
                      <span>GH₵ {getTotalPrice().toFixed(2)}</span>
                    </ListGroup.Item>
                    <ListGroup.Item className="d-flex justify-content-between px-0">
                      <span>Shipping Fee</span>
                      <span>GH₵ 0.00</span>
                    </ListGroup.Item>
                    <ListGroup.Item className="d-flex justify-content-between px-0 fw-bold">
                      <span>Total</span>
                      <span>GH₵ {getTotalPrice().toFixed(2)}</span>
                    </ListGroup.Item>
                  </ListGroup>
                </Card.Body>
                <Card.Footer className="bg-white">
                  <Button 
                    variant="primary" 
                    className="w-100"
                    size="lg"
                    onClick={handleCheckout}
                    disabled={loading}
                  >
                    <FaCreditCard className="me-2" />
                    {loading ? 'Processing...' : 'Proceed to Checkout'}
                  </Button>
                  
                  <div className="mt-3 text-center">
                    <small className="text-muted">
                      By proceeding to checkout, you agree to our <Link to="/terms">Terms of Service</Link> and <Link to="/privacy">Privacy Policy</Link>.
                    </small>
                  </div>
                </Card.Footer>
              </Card>
              
              {/* Payment Methods */}
              <Card className="shadow-sm mt-4">
                <Card.Body>
                  <h5>Accepted Payment Methods</h5>
                  <div className="d-flex justify-content-between mt-3">
                    <div className="text-center">
                      <div className="border rounded p-2 mb-1 d-flex align-items-center justify-content-center" style={{height: '40px'}}>
                        <span className="text-success fw-bold">MM</span>
                      </div>
                      <small>Mobile Money</small>
                    </div>
                    <div className="text-center">
                      <div className="border rounded p-2 mb-1 d-flex align-items-center justify-content-center" style={{height: '40px'}}>
                        <span className="text-primary fw-bold">💵</span>
                      </div>
                      <small>Cash</small>
                    </div>
                    <div className="text-center">
                      <div className="border rounded p-2 mb-1 d-flex align-items-center justify-content-center" style={{height: '40px'}}>
                        <span className="text-info fw-bold">🏦</span>
                      </div>
                      <small>Bank Transfer</small>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
      </Container>
    </Layout>
  );
};

export default Cart;
